import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Drawer, Box } from '@mui/material';
import { getSessionToken } from '../../../auth';
import { User, Region } from '../types';
import {
  AssignableRole,
  UserSessionRow,
  fetchUserDetail,
  fetchUserSessions as fetchSessionsApi,
  revokeUserSession,
  revokeAllUserSessions,
  uploadUserAvatar,
  saveUserPersonalData,
  resetUserPassword,
  updateRole,
  updateRegion,
  updatePharmacy
} from '../allUsersApi';
import { impersonateAndRedirect } from '../utils/impersonation';
import { compressAvatar } from '../../../profile/avatarCompressor';
import { changePassword } from '../../../profile/profileApi';
import {
  formatPhonePlus7,
  formatPhoneWithSpaces,
  cleanPhoneForBackend,
  handlePhoneInputChange,
  isFilledPhone,
  isMobileWhatsAppPhone
} from './UserTableRow';
import { canUserEditTarget } from '../utils/permissions';
import { notify } from '../../../notifications';
import { apiErrorMessage } from '../../reports/edit/editApi';

import {
  UserDetailSectionId,
  UserDetailHeader,
  UserDetailNav,
  UserDetailSidebar,
  UserProfileSection,
  UserSecuritySection,
  UserStaffSection,
  UserPharmaciesSection,
  UserGeoSection,
  UserMailSection,
  UserBiometricsSection,
  UserSessionsSection,
  UserDangerZoneSection,
  UserDeleteDialog
} from './UserDetail';

export interface UserDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  regions: Region[];
  /** GET /api/v2/users/roles: assignable уже посчитан сервером по правилу «ниже моей» */
  roles: AssignableRole[];
  /** Сервер вернул точку в /api/v2/users-staff/counts: раздел «Сотрудники точки» доступен */
  showStaff?: boolean;
  onRefetchUsers: () => void;
  onUpdateUser?: (updatedFields: Partial<User>) => void;
  onToggleActivation: (user: User) => void;
  onTogglePOS: (user: User) => void;
  onToggleReports: (user: User) => void;
  /** true - сервер удалил сотрудника */
  onDeleteUser: (user: User) => Promise<boolean>;
}

const TOGGLE_BLOCKED_REASON = 'Сотрудник вне вашей зоны управления';

export const UserDetailDrawer: React.FC<UserDetailDrawerProps> = ({
  open,
  onClose,
  user,
  regions,
  roles,
  showStaff = false,
  onRefetchUsers,
  onUpdateUser,
  onToggleActivation,
  onTogglePOS,
  onToggleReports,
  onDeleteUser
}) => {
  const lastUserIdRef = useRef<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isScrollingProgrammaticallyRef = useRef(false);

  // Активная секция для липкой навигации (Scrollspy)
  const [activeSection, setActiveSection] = useState<UserDetailSectionId>('profile');

  // Режим редактирования карточки контактов (Битрикс24-стиль)
  const [isEditingContact, setIsEditingContact] = useState(false);

  // Форма персональных данных
  const [fullName, setFullName] = useState('');
  const [userphone, setUserphone] = useState('');
  const [workPhone, setWorkPhone] = useState('');
  const [iin, setIin] = useState('');
  const [showIIN, setShowIIN] = useState(false);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Форма пароля; текущий пароль нужен только при смене своего
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Форма роли, региона, аптеки
  const [role, setRole] = useState('');
  const [cityId, setCityId] = useState('');
  const [pharmacyNum, setPharmacyNum] = useState('');
  const [myPharmacy, setMyPharmacy] = useState('');
  const [newPharmacyToAdd, setNewPharmacyToAdd] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatarUrl || null);
  const [waMenuAnchor, setWaMenuAnchor] = useState<null | HTMLElement>(null);

  // Детальные данные профиля и актуальные координаты геолокации
  const [geoData, setGeoData] = useState<{
    latitude: string | null;
    longitude: string | null;
    accuracy: number | null;
    address: string;
    pharmacyAddress: string;
    geoUpdatedAt: string | null;
    geoGuid: string | null;
  }>({
    latitude: user?.latitude || null,
    longitude: user?.longitude || null,
    accuracy: user?.accuracy ?? null,
    address: user?.address || '',
    pharmacyAddress: user?.pharmacyAddress || user?.address || '',
    geoUpdatedAt: user?.geoUpdatedAt || null,
    geoGuid: user?.geoGuid || null
  });
  const [loadingGeo, setLoadingGeo] = useState(false);

  // Модалка подтверждения удаления
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Имперсонация и управление сессиями
  const [impersonating, setImpersonating] = useState(false);
  const [sessions, setSessions] = useState<UserSessionRow[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState<number | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const currentUsername = (localStorage.getItem('username') || '').trim().toLowerCase();
  const isSelf = Boolean(user && currentUsername && (user.username || '').trim().toLowerCase() === currentUsername);

  // Все флаги считает сервер (permissionService: право, скоуп и ранг) - здесь только чтение
  const canEditUser = user ? canUserEditTarget(user) : false;
  const canEditRole = user?.canEditRole === true;
  const canEditPharmacyNum = user?.canEditPharmacy === true;
  const canViewIIN = user?.canViewIin === true;
  const canEditPassword = user?.canEditPassword === true;
  const canToggleStatus = user?.canToggleStatus === true;
  const canDelete = user?.canDelete === true;
  const canImpersonate = user?.canImpersonate === true;
  const canViewActivity = user?.canViewActivity === true;
  // Свои сессии видны и завершаются всегда; чужие - как в authV2: просмотр по view_activity, завершение по toggle_status
  const canSeeSessions = isSelf || canViewActivity;
  const canRevokeSessions = isSelf || canToggleStatus;

  const fetchUserSessions = useCallback(async () => {
    if (!user?.id || !canSeeSessions) return;
    setLoadingSessions(true);
    try {
      setSessions(await fetchSessionsApi(user.id));
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось загрузить сессии пользователя'));
    } finally {
      setLoadingSessions(false);
    }
  }, [user?.id, canSeeSessions]);

  const handleRevokeSingleSession = async (sessionId: number) => {
    setRevokingSessionId(sessionId);
    try {
      notify.success(await revokeUserSession(sessionId));
      fetchUserSessions();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось завершить сессию'));
    } finally {
      setRevokingSessionId(null);
    }
  };

  const handleRevokeAllSessions = async () => {
    if (!user?.id) return;
    setRevokingAll(true);
    try {
      // Свою текущую сессию сохраняем, иначе оператора выбросит из портала
      notify.success(await revokeAllUserSessions(user.id, isSelf ? getSessionToken() : null));
      fetchUserSessions();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось завершить сессии'));
    } finally {
      setRevokingAll(false);
    }
  };

  // Вход под сотрудником без подтверждения: право есть у единиц, а вернуться можно одной кнопкой
  const handleImpersonate = async () => {
    if (!user || impersonating) return;
    setImpersonating(true);
    try {
      await impersonateAndRedirect(user);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось войти под пользователем'));
      setImpersonating(false);
    }
  };

  // Актуальные геоданные пользователя: только GET /api/v2/users/:id (сервер проверяет users.view)
  const fetchLiveGeoDetails = async (targetUser: User) => {
    if (!targetUser) return;
    setLoadingGeo(true);
    try {
      const u = await fetchUserDetail(targetUser.id);
      setGeoData({
        latitude: u.latitude || null,
        longitude: u.longitude || null,
        accuracy: u.accuracy != null ? Number(u.accuracy) : null,
        address: u.address || '',
        pharmacyAddress: u.pharmacyAddress || u.address || '',
        geoUpdatedAt: u.geoUpdatedAt || null,
        geoGuid: u.geoGuid || null
      });
      if (u.workPhone && isFilledPhone(u.workPhone) && (!targetUser.workPhone || !isFilledPhone(targetUser.workPhone))) {
        setWorkPhone(formatPhonePlus7(u.workPhone));
      }
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось загрузить геоданные пользователя'));
    } finally {
      setLoadingGeo(false);
    }
  };

  // Инициализация при выборе пользователя
  useEffect(() => {
    if (user) {
      const isNewUser = lastUserIdRef.current !== user.id;
      lastUserIdRef.current = user.id;

      if (isNewUser) {
        setFullName(user.fullName || '');
        setUserphone(isFilledPhone(user.userphone) ? formatPhonePlus7(user.userphone) : '');
        setWorkPhone(isFilledPhone(user.workPhone) ? formatPhonePlus7(user.workPhone) : '');
        setIin(user.iin || '');
        setRole(user.role || 'user');
        setCityId(user.cityId ? String(user.cityId) : '');
        setPharmacyNum(user.pharmacyNum ? String(user.pharmacyNum) : '');
        setMyPharmacy(user.myPharmacy || '');
        setAvatarUrl(user.avatarUrl || null);
        setCurrentPassword('');
        setNewPassword('');
        setShowPassword(false);
        setShowIIN(false);
        setIsEditingContact(false);
        setActiveSection('profile');
        setSessions([]);

        setGeoData({
          latitude: user.latitude || null,
          longitude: user.longitude || null,
          accuracy: user.accuracy != null ? Number(user.accuracy) : null,
          address: user.address || '',
          pharmacyAddress: user.pharmacyAddress || user.address || '',
          geoUpdatedAt: user.geoUpdatedAt || null,
          geoGuid: user.geoGuid || null
        });

        fetchLiveGeoDetails(user);
        fetchUserSessions();

        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      } else {
        if (user.avatarUrl) setAvatarUrl(user.avatarUrl);
        if (!isEditingContact) {
          setFullName(user.fullName || '');
          setUserphone(isFilledPhone(user.userphone) ? formatPhonePlus7(user.userphone) : '');
          setWorkPhone(isFilledPhone(user.workPhone) ? formatPhonePlus7(user.workPhone) : '');
          setIin(user.iin || '');
        }
      }
    } else {
      lastUserIdRef.current = null;
    }
  }, [user, fetchUserSessions]);

  // Секции без доступа не рендерятся, поэтому ни навигация, ни scrollspy о них не знают
  const visibleSections: UserDetailSectionId[] = [
    'profile',
    ...((canEditPassword || isSelf || canEditRole || canEditPharmacyNum) ? ['security' as UserDetailSectionId] : []),
    ...(showStaff ? ['staff' as UserDetailSectionId] : []),
    'pharmacies',
    'geo',
    'mail',
    'biometrics',
    ...(canSeeSessions ? ['sessions' as UserDetailSectionId] : []),
    ...(canDelete ? ['danger' as UserDetailSectionId] : [])
  ];

  // Плавный скролл к выбранной секции
  const handleScrollToSection = (sectionId: UserDetailSectionId) => {
    setActiveSection(sectionId);
    const el = document.getElementById(`user-section-${sectionId}`);
    if (el) {
      isScrollingProgrammaticallyRef.current = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        isScrollingProgrammaticallyRef.current = false;
      }, 700);
    }
  };

  // Scrollspy: отслеживание активного блока при ручном скролле
  const handleContainerScroll = () => {
    if (isScrollingProgrammaticallyRef.current || !scrollContainerRef.current) return;
    const containerRect = scrollContainerRef.current.getBoundingClientRect();

    let matchedSection = visibleSections[0];
    for (const sId of visibleSections) {
      const el = document.getElementById(`user-section-${sId}`);
      if (el) {
        const elRect = el.getBoundingClientRect();
        if (elRect.top - containerRect.top <= 120) {
          matchedSection = sId;
        }
      }
    }
    setActiveSection(matchedSection);
  };

  if (!user) return null;

  // Статусы берутся из списка: useUserActions меняет их там сразу и откатывает при отказе сервера
  const deactivation = user.deactivation ?? 0;
  const pos = user.pos ?? 0;
  const reports = user.reports ?? 0;

  const displayName = fullName.trim() || user.username;
  const roleName = roles.find(r => r.slug === role)?.name || user.roleName || role;
  // Только подпись точки, на доступ не влияет
  const isStorage = role === 'storage';
  const locationBadge = pharmacyNum && pharmacyNum !== '-' && pharmacyNum !== 'NaN'
    ? (isStorage ? `Склад №${pharmacyNum}` : `Аптека №${pharmacyNum}`)
    : 'Офис / Без привязки';

  const rawMail = user.mailEmail || user.mailLogin;
  const mailAddress = rawMail
    ? (rawMail.includes('@') ? rawMail : `${rawMail.replace(/@zerdegroup\.kz$/i, '')}@zerdegroup.kz`)
    : null;

  const isUserphoneFilled = isFilledPhone(userphone);
  const isWorkPhoneFilled = isFilledPhone(workPhone);
  const displayUserphone = isUserphoneFilled ? formatPhonePlus7(userphone) : null;
  const displayWorkPhone = isWorkPhoneFilled ? formatPhonePlus7(workPhone) : null;
  const isUserphoneWA = isMobileWhatsAppPhone(userphone);
  const isWorkPhoneWA = isMobileWhatsAppPhone(workPhone);
  const currentRegionName = regions.find(r => String(r.id) === String(cityId))?.name || (cityId ? `${cityId}` : user.regionName || '-');

  // Загрузка аватарки со сжатием 512x512
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditUser) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notify.warning('Выберите файл изображения (JPG, PNG, WebP)');
      return;
    }

    try {
      setUploadingAvatar(true);
      const compressedDataUrl = await compressAvatar(file, { targetSize: 512, quality: 0.85 });
      setAvatarUrl(await uploadUserAvatar(user.id, compressedDataUrl));
      notify.success('Фотография профиля обновлена');
      onRefetchUsers();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось сохранить аватар'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Сохранение персональных данных
  const handleSavePersonal = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canEditUser) return;
    setSavingPersonal(true);
    const cleanedUserphone = isFilledPhone(userphone) ? formatPhoneWithSpaces(userphone) : '';
    const cleanedWorkPhone = isFilledPhone(workPhone) ? formatPhoneWithSpaces(workPhone) : '';

    const backendUserphone = isFilledPhone(userphone) ? cleanPhoneForBackend(userphone) : '';
    const backendWorkPhone = isFilledPhone(workPhone) ? cleanPhoneForBackend(workPhone) : '';

    try {
      // ИИН без users.view_iin не отправляется: поле скрыто, а сервер все равно его отклонит
      const message = await saveUserPersonalData(user.id, {
        fullName,
        userphone: backendUserphone,
        workPhone: backendWorkPhone,
        ...(canViewIIN ? { iin } : {})
      });

      if (onUpdateUser) {
        onUpdateUser({
          fullName,
          userphone: cleanedUserphone,
          workPhone: cleanedWorkPhone,
          ...(canViewIIN ? { iin } : {})
        });
      }
      notify.success(message);
      setIsEditingContact(false);
      onRefetchUsers();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Ошибка сохранения персональных данных'));
    } finally {
      setSavingPersonal(false);
    }
  };

  // Смена пароля: свой - как в профиле, с текущим паролем; чужой - PUT /api/v2/users/:id/password
  const handleSavePassword = async () => {
    if (!isSelf && !canEditPassword) return;
    if (isSelf && !currentPassword) {
      notify.warning('Введите текущий пароль');
      return;
    }
    if (!newPassword.trim()) {
      notify.warning('Введите новый пароль');
      return;
    }
    setSavingPassword(true);
    try {
      if (isSelf) {
        const result = await changePassword(currentPassword, newPassword);
        notify.success(result.message);
        setCurrentPassword('');
      } else {
        notify.success(await resetUserPassword(user.id, newPassword.trim()));
      }
      setNewPassword('');
      onRefetchUsers();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Ошибка при смене пароля'));
    } finally {
      setSavingPassword(false);
    }
  };

  // Сохранение настроек доступа: роль - users.edit_role, регион и аптека - users.edit_pharmacy
  const handleSaveSettings = async () => {
    const roleChanged = canEditRole && Boolean(role) && role !== user.role;
    const regionChanged = canEditPharmacyNum && cityId !== String(user.cityId || '');
    const pharmacyChanged = canEditPharmacyNum && Boolean(pharmacyNum) && pharmacyNum !== String(user.pharmacyNum || '');
    if (!roleChanged && !regionChanged && !pharmacyChanged) {
      notify.info('Изменений нет');
      return;
    }
    setSavingSettings(true);
    try {
      if (roleChanged) await updateRole(user.id, role);
      if (regionChanged) await updateRegion(user.id, cityId ? Number(cityId) : null);
      if (pharmacyChanged) await updatePharmacy(user.id, { pharmacyNum });

      if (onUpdateUser) {
        onUpdateUser({
          ...(roleChanged ? { role } : {}),
          ...(regionChanged ? { cityId } : {}),
          ...(pharmacyChanged ? { pharmacyNum } : {})
        });
      }
      notify.success('Параметры доступа обновлены');
      onRefetchUsers();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Ошибка обновления настроек'));
      // Часть полей могла сохраниться до ошибки - перечитываем список
      onRefetchUsers();
    } finally {
      setSavingSettings(false);
    }
  };

  // Подчиненные аптеки: сервер меняет myPharmacy тем же правом users.edit_pharmacy
  const handleSavePharmacies = async (updatedList: string) => {
    if (!canEditPharmacyNum) return;
    try {
      await updatePharmacy(user.id, { myPharmacy: updatedList });
      setMyPharmacy(updatedList);
      notify.success('Список подчиненных аптек сохранен');
      onRefetchUsers();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Ошибка сохранения списка аптек'));
    }
  };

  const handleAddPharmacyChip = () => {
    const val = newPharmacyToAdd.trim().replace(/\D/g, '');
    if (!val) return;

    const currentArray = myPharmacy
      ? myPharmacy.split(',').map(s => s.trim()).filter(Boolean)
      : [];

    if (currentArray.indexOf(val) !== -1) {
      notify.info(`Аптека №${val} уже в списке`);
      setNewPharmacyToAdd('');
      return;
    }

    const next = currentArray.concat(val).join(',');
    handleSavePharmacies(next);
    setNewPharmacyToAdd('');
  };

  const handleRemovePharmacyChip = (numToRemove: string) => {
    const currentArray = myPharmacy
      ? myPharmacy.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    const next = currentArray.filter(n => n !== numToRemove).join(',');
    handleSavePharmacies(next);
  };

  // Быстрые переключатели: оптимистичное обновление и откат делает useUserActions
  const handleToggleActivation = () => {
    if (!canToggleStatus) return;
    onToggleActivation(user);
  };

  const handleTogglePOS = () => {
    if (!canToggleStatus) return;
    onTogglePOS(user);
  };

  const handleToggleReports = () => {
    if (!canToggleStatus || pos === 0) return;
    onToggleReports(user);
  };

  // Удаление пользователя (строго ранг 100); карточка закрывается только после успеха
  const handleConfirmDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      if (await onDeleteUser(user)) {
        setOpenDeleteModal(false);
        onClose();
      }
    } finally {
      setDeleting(false);
    }
  };

  // Координаты для карты (берем из geoData или fallback на user)
  const currentLatStr = geoData.latitude || user.latitude;
  const currentLngStr = geoData.longitude || user.longitude;
  const currentAddress = geoData.pharmacyAddress || geoData.address || user.pharmacyAddress || user.address || '';
  const currentAccuracy = geoData.accuracy != null ? geoData.accuracy : (user.accuracy != null ? user.accuracy : null);
  const currentGeoUpdatedAt = geoData.geoUpdatedAt || user.geoUpdatedAt || null;

  const lat = currentLatStr ? parseFloat(currentLatStr) : null;
  const lng = currentLngStr ? parseFloat(currentLngStr) : null;
  const hasValidCoords = lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '92%', md: '86%', lg: '1060px', xl: '1160px' },
            bgcolor: '#f8fafc',
            boxShadow: '-12px 0 40px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        {/* Шапка в стиле профайла Zerde */}
        <UserDetailHeader
          user={user}
          displayName={displayName}
          canToggleStatus={canToggleStatus}
          canImpersonate={canImpersonate}
          toggleBlockedReason={TOGGLE_BLOCKED_REASON}
          deactivation={deactivation}
          pos={pos}
          reports={reports}
          isUserphoneWA={isUserphoneWA}
          isWorkPhoneWA={isWorkPhoneWA}
          displayUserphone={displayUserphone}
          displayWorkPhone={displayWorkPhone}
          userphone={userphone}
          workPhone={workPhone}
          waMenuAnchor={waMenuAnchor}
          setWaMenuAnchor={setWaMenuAnchor}
          onToggleActivation={handleToggleActivation}
          onTogglePOS={handleTogglePOS}
          onToggleReports={handleToggleReports}
          impersonating={impersonating}
          onImpersonate={handleImpersonate}
          onClose={onClose}
        />

        {/* Липкая лента навигации со Scrollspy */}
        <UserDetailNav
          activeSection={activeSection}
          onSectionClick={handleScrollToSection}
          visibleSections={visibleSections}
        />

        {/* Основная область: слева Sticky-колонка (аватар, сервисы, Face ID), справа стек секций */}
        <Box
          ref={scrollContainerRef}
          onScroll={handleContainerScroll}
          sx={{
            p: { xs: 2, sm: 3.5 },
            flexGrow: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            gap: 3.5
          }}
        >
          {/* ── ЛЕВАЯ КОЛОНКА (Sticky) ────────────────────────── */}
          <Box
            sx={{
              width: { xs: '100%', md: 300, lg: 320 },
              flexShrink: 0,
              position: { xs: 'static', md: 'sticky' },
              top: 0,
              alignSelf: 'flex-start'
            }}
          >
            <UserDetailSidebar
              user={user}
              roleName={roleName}
              deactivation={deactivation}
              avatarUrl={avatarUrl}
              fullName={fullName}
              canEditUser={canEditUser}
              canResetPassword={canEditPassword || isSelf}
              uploadingAvatar={uploadingAvatar}
              handleAvatarUpload={handleAvatarUpload}
              pos={pos}
              reports={reports}
              onScrollToSection={handleScrollToSection}
            />
          </Box>

          {/* ── ПРАВАЯ КОЛОНКА: СТЕК БЛОКОВ ДРУГ ПОД ДРУГОМ ──── */}
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              width: { xs: '100%', md: 'calc(100% - 340px)' },
              display: 'flex',
              flexDirection: 'column',
              gap: 3.5
            }}
          >
            {/* Секция 1: Профиль и контакты */}
            <UserProfileSection
              user={user}
              displayName={displayName}
              roleName={roleName}
              locationBadge={locationBadge}
              currentRegionName={currentRegionName}
              mailAddress={mailAddress}
              isEditingContact={isEditingContact}
              setIsEditingContact={setIsEditingContact}
              fullName={fullName}
              setFullName={setFullName}
              userphone={userphone}
              setUserphone={setUserphone}
              workPhone={workPhone}
              setWorkPhone={setWorkPhone}
              iin={iin}
              setIin={setIin}
              showIIN={showIIN}
              setShowIIN={setShowIIN}
              savingPersonal={savingPersonal}
              handleSavePersonal={handleSavePersonal}
              handlePhoneInputChange={handlePhoneInputChange}
              canEditUser={canEditUser}
              canViewIIN={canViewIIN}
              displayUserphone={displayUserphone}
              displayWorkPhone={displayWorkPhone}
              isUserphoneWA={isUserphoneWA}
              isWorkPhoneWA={isWorkPhoneWA}
              myPharmacy={myPharmacy}
              currentAddress={currentAddress}
              loadingGeo={loadingGeo}
            />

            {/* Секция 2: Доступ и безопасность */}
            {visibleSections.indexOf('security') !== -1 && (
              <UserSecuritySection
                canEditPassword={canEditPassword}
                canEditRole={canEditRole}
                canEditPharmacyNum={canEditPharmacyNum}
                isSelf={isSelf}
                currentPassword={currentPassword}
                setCurrentPassword={setCurrentPassword}
                newPassword={newPassword}
                setNewPassword={setNewPassword}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                savingPassword={savingPassword}
                handleSavePassword={handleSavePassword}
                role={role}
                setRole={setRole}
                cityId={cityId}
                setCityId={setCityId}
                pharmacyNum={pharmacyNum}
                setPharmacyNum={setPharmacyNum}
                regions={regions}
                roles={roles}
                savingSettings={savingSettings}
                handleSaveSettings={handleSaveSettings}
              />
            )}

            {/* Сотрудники точки (§44): только у точек в зоне users.staff.view */}
            {showStaff && <UserStaffSection masterId={user.id} />}

            {/* Секция 3: Подчиненные аптеки */}
            <UserPharmaciesSection
              canEdit={canEditPharmacyNum}
              newPharmacyToAdd={newPharmacyToAdd}
              setNewPharmacyToAdd={setNewPharmacyToAdd}
              handleAddPharmacyChip={handleAddPharmacyChip}
              handleRemovePharmacyChip={handleRemovePharmacyChip}
              myPharmacy={myPharmacy}
            />

            {/* Секция 4: Геолокация */}
            <UserGeoSection
              user={user}
              loadingGeo={loadingGeo}
              onRefreshGeo={() => fetchLiveGeoDetails(user)}
              currentAddress={currentAddress}
              hasValidCoords={hasValidCoords}
              currentLatStr={currentLatStr || null}
              currentLngStr={currentLngStr || null}
              currentAccuracy={currentAccuracy}
              currentGeoUpdatedAt={currentGeoUpdatedAt}
              lat={lat}
              lng={lng}
            />

            {/* Секция 5: Корпоративная почта */}
            <UserMailSection
              mailAddress={mailAddress}
              hasMailSession={Boolean(user.hasMailSession)}
            />

            {/* Секция 6: Биометрия (Face ID) */}
            <UserBiometricsSection user={user} />

            {/* Секция 7: Сессии и активность */}
            {canSeeSessions && (
              <UserSessionsSection
                user={user}
                canImpersonate={canImpersonate}
                canRevokeSessions={canRevokeSessions}
                displayName={displayName}
                impersonating={impersonating}
                onImpersonate={handleImpersonate}
                sessions={sessions}
                loadingSessions={loadingSessions}
                onRefreshSessions={fetchUserSessions}
                revokingAll={revokingAll}
                onRevokeAllSessions={handleRevokeAllSessions}
                revokingSessionId={revokingSessionId}
                onRevokeSingleSession={handleRevokeSingleSession}
              />
            )}

            {/* Секция 8: Опасная зона (только для Главного Администратора - ранг 100) */}
            {canDelete && (
              <UserDangerZoneSection onOpenDeleteModal={() => setOpenDeleteModal(true)} />
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Модальное окно подтверждения удаления */}
      {canDelete && (
        <UserDeleteDialog
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
          username={user.username}
          displayName={displayName}
          deleting={deleting}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
};

export default UserDetailDrawer;
